/**
 * Slice 2 P3 / P5 — Hospitable connect · pick unit · Import / Link
 * Exact Claude Design. Same backend actions as before.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useCreateProperty, useProperties, useUpdateProperty } from "@/hooks/useProperties";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

type HospitableUnit = { id: string; name: string; address: string; picture_url?: string | null };

async function invokeHospitable(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("hospitable-proxy", { body });
  if (error) throw new Error(error.message || "Hospitable request failed");
  if (data?.error) throw new Error(String(data.error));
  return data as Record<string, unknown>;
}

type Props = {
  /** When set = P5 Link on existing property. When unset = P3 Add/Import. */
  propertyId?: string;
  /** Current property name/UUID for P5 linked state */
  linkedHospitableId?: string | null;
  linkedPropertyName?: string | null;
  allowCreate?: boolean;
  /** Called after successful import/link (close dialog) */
  onDone?: () => void;
  /** Quiet fallback to manual wizard */
  onAddManually?: () => void;
};

export default function HospitableConnectCard({
  propertyId,
  linkedHospitableId,
  linkedPropertyName,
  allowCreate = !propertyId,
  onDone,
  onAddManually,
}: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: properties = [] } = useProperties();
  const updateProperty = useUpdateProperty();
  const createProperty = useCreateProperty();

  const [connected, setConnected] = useState(false);
  const [patInput, setPatInput] = useState("");
  const [units, setUnits] = useState<HospitableUnit[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [showTokenUpdate, setShowTokenUpdate] = useState(false);

  const isLinkMode = Boolean(propertyId);
  const alreadyLinked = Boolean(linkedHospitableId?.trim());

  const linkedHospitableIds = useMemo(() => {
    const set = new Set<string>();
    for (const p of properties as Array<{ id: string; hospitable_property_id?: string | null }>) {
      const hid = String(p.hospitable_property_id || "").trim();
      if (hid) set.add(hid);
    }
    return set;
  }, [properties]);

  const refreshStatus = useCallback(async () => {
    if (!user) return;
    setStatusLoading(true);
    try {
      const data = await invokeHospitable({ action: "status" });
      setConnected(Boolean(data.connected));
    } catch {
      setConnected(false);
    } finally {
      setStatusLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  const loadUnits = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await invokeHospitable({ action: "list_properties" });
      const list = Array.isArray(data.properties) ? (data.properties as HospitableUnit[]) : [];
      setUnits(list);
      setSelectedId((prev) => {
        if (prev && list.some((u) => u.id === prev)) return prev;
        const firstFree = list.find((u) => !linkedHospitableIds.has(u.id) || u.id === linkedHospitableId);
        return firstFree?.id || "";
      });
    } catch (err) {
      toast({
        title: "Could not list properties",
        description: err instanceof Error ? err.message : "Failed",
        variant: "destructive",
      });
    } finally {
      setLoadingList(false);
    }
  }, [linkedHospitableIds, linkedHospitableId]);

  useEffect(() => {
    if (connected && !(isLinkMode && alreadyLinked)) void loadUnits();
  }, [connected, isLinkMode, alreadyLinked, loadUnits]);

  const savePat = async () => {
    setBusy(true);
    try {
      const data = await invokeHospitable({
        action: "save_pat",
        pat: patInput,
        ...(propertyId ? { property_id: propertyId } : {}),
      });
      setConnected(Boolean(data.connected));
      setPatInput("");
      setShowTokenUpdate(false);
      toast({ title: "Connected" });
      await loadUnits();
    } catch (err) {
      toast({
        title: "Could not connect",
        description: err instanceof Error ? err.message : "Failed",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const selected = units.find((u) => u.id === selectedId) || null;

  const linkSelected = async () => {
    if (!selected) return;
    if (!propertyId && !allowCreate) return;
    const taken = linkedHospitableIds.has(selected.id) && selected.id !== linkedHospitableId;
    if (taken) {
      toast({ title: "Already linked", description: "That unit is already in the hub.", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      if (propertyId) {
        await updateProperty.mutateAsync({
          propertyId,
          hospitable_property_id: selected.id,
          name: selected.name || undefined,
          address: selected.address || undefined,
        });
        toast({ title: "Linked", description: selected.name });
        onDone?.();
      } else {
        const created = await createProperty.mutateAsync({
          name: selected.name,
          address: selected.address || undefined,
          hospitable_property_id: selected.id,
        });
        toast({ title: "Imported", description: selected.name });
        onDone?.();
        if (created?.id) navigate(`/properties/${created.id}`);
      }
    } catch (err) {
      toast({
        title: "Failed",
        description: err instanceof Error ? err.message : "Failed",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  const title = isLinkMode ? "Hospitable" : "Add property";
  const primaryLabel = !connected ? "Connect" : isLinkMode ? "Link" : "Import";
  const primaryDisabled =
    busy ||
    statusLoading ||
    (!connected && !patInput.trim()) ||
    (connected && !(isLinkMode && alreadyLinked) && !selected);

  const onPrimary = () => {
    if (!connected) void savePat();
    else void linkSelected();
  };

  // P5 linked state
  if (isLinkMode && alreadyLinked) {
    const shortId =
      linkedHospitableId && linkedHospitableId.length > 12
        ? `${linkedHospitableId.slice(0, 8)}…${linkedHospitableId.slice(-4)}`
        : linkedHospitableId;
    return (
      <div className="flex flex-col">
        <div className="border-b-[1.5px] border-[#e6edf1] px-6 py-5">
          <div className="text-[20px] font-bold tracking-[-0.3px] text-[#0f1720]">{title}</div>
        </div>
        <div className="flex flex-col gap-4 px-6 py-[22px]">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#d7efec]">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#15776d" strokeWidth="2.8" strokeLinecap="round">
                <path d="M5 13l4.5 4.5L19 7" />
              </svg>
            </div>
            <div className="text-[18px] font-bold text-[#15776d]">Linked</div>
          </div>
          <div className="flex flex-col gap-1 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-4 py-3.5">
            <div className="text-[15px] font-bold text-[#0f1720]">{linkedPropertyName || "Property"}</div>
            <div className="font-mono text-[12px] text-[#94a3b8]">{shortId}</div>
          </div>
          {showTokenUpdate ? (
            <div className="flex flex-col gap-2.5">
              <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Token</Label>
              <Input
                type="password"
                autoComplete="off"
                value={patInput}
                onChange={(e) => setPatInput(e.target.value)}
                placeholder="hos_pat_…"
                className="h-[54px] rounded-[13px] border-[1.5px] border-[#24a89a] font-mono text-[16px] font-medium shadow-none focus-visible:ring-[#24a89a]"
              />
              <p className="text-[13px] font-medium text-[#64748b]">
                Paste a new Hospitable personal access token. Sync uses this.
              </p>
              <Button
                type="button"
                disabled={busy || !patInput.trim()}
                onClick={() => void savePat()}
                className="h-[54px] rounded-[14px] bg-[#24a89a] text-[17px] font-bold text-white shadow-none hover:bg-[#1f958a]"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save token"}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              className="self-start text-[14px] font-bold text-[#15776d] hover:text-[#24a89a]"
              onClick={() => setShowTokenUpdate(true)}
            >
              Update token
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b-[1.5px] border-[#e6edf1] px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="text-[20px] font-bold tracking-[-0.3px] text-[#0f1720]">{title}</div>
          {connected ? (
            <div className="flex items-center gap-1.5 rounded-full bg-[#d7efec] px-2.5 py-1 text-[13px] font-bold text-[#15776d]">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#15776d" strokeWidth="3" strokeLinecap="round">
                <path d="M5 13l4.5 4.5L19 7" />
              </svg>
              Connected
            </div>
          ) : null}
        </div>
      </div>

      {statusLoading ? (
        <div className="flex items-center justify-center gap-2 px-6 py-12 text-[14px] font-medium text-[#64748b]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking…
        </div>
      ) : !connected ? (
        <div className="flex flex-col gap-2.5 px-6 py-[22px]">
          <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Token</Label>
          <Input
            type="password"
            autoComplete="off"
            value={patInput}
            onChange={(e) => setPatInput(e.target.value)}
            placeholder="hos_pat_…"
            className="h-[54px] rounded-[13px] border-[1.5px] border-[#24a89a] font-mono text-[16px] font-medium shadow-none focus-visible:ring-[#24a89a]"
          />
          <p className="text-[13px] font-medium text-[#64748b]">Hospitable personal access token.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 px-6 pb-2 pt-5">
          <div className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Units</div>
          {loadingList && units.length === 0 ? (
            <div className="flex items-center gap-2 py-6 text-[14px] text-[#64748b]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : units.length === 0 ? (
            <p className="py-6 text-[14px] font-medium text-[#64748b]">No units</p>
          ) : (
            <div className="flex max-h-64 flex-col gap-2.5 overflow-y-auto">
              {units.map((u) => {
                const taken = linkedHospitableIds.has(u.id) && u.id !== linkedHospitableId;
                const selected = selectedId === u.id;
                return (
                  <button
                    key={u.id}
                    type="button"
                    disabled={taken}
                    onClick={() => !taken && setSelectedId(u.id)}
                    className={cn(
                      "flex items-center gap-3 rounded-[14px] border-[1.5px] px-3.5 py-3 text-left",
                      taken
                        ? "cursor-not-allowed border-[#eef2f5] bg-[#fbfdfe] opacity-70"
                        : selected
                          ? "border-[#24a89a] bg-[#eaf6f4]"
                          : "border-[#e6edf1] bg-white hover:border-[#d7e0e6]",
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-5 w-5 flex-none items-center justify-center rounded-full border-[1.5px]",
                        selected ? "border-[#24a89a]" : "border-[#d7e0e6]",
                      )}
                    >
                      {selected ? <div className="h-2.5 w-2.5 rounded-full bg-[#24a89a]" /> : null}
                    </div>
                    <div className="h-11 w-11 flex-none overflow-hidden rounded-[10px] bg-[#eef3f6]">
                      {u.picture_url ? (
                        <img
                          src={u.picture_url}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[#94a3b8]">
                          <Building2 className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[16px] font-bold text-[#0f1720]">{u.name}</div>
                      <div className="truncate text-[13px] font-medium text-[#64748b]">{u.address || "—"}</div>
                    </div>
                    {taken ? (
                      <span className="rounded-full bg-[#eef3f6] px-2.5 py-1.5 text-[12px] font-bold tracking-[0.4px] text-[#64748b]">
                        Linked
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-3.5 flex items-center gap-3.5 border-t-[1.5px] border-[#e6edf1] px-6 py-[18px]">
        <Button
          type="button"
          disabled={primaryDisabled}
          onClick={onPrimary}
          className="h-[54px] flex-1 rounded-[14px] bg-[#24a89a] text-[17px] font-bold text-white shadow-none hover:bg-[#1f958a]"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : primaryLabel}
        </Button>
        {!isLinkMode && onAddManually ? (
          <button
            type="button"
            className="text-[14px] font-medium text-[#64748b] hover:text-[#24a89a]"
            onClick={onAddManually}
          >
            Add manually
          </button>
        ) : null}
      </div>
    </div>
  );
}
